import {getPostHogClient} from './posthogClient';

export interface PostHogEventProperties {
  [key: string]: unknown;
}

export interface PostHogUserProperties {
  email?: string;
  name?: string;
  plan?: string;
  company?: string;
  [key: string]: unknown;
}

export const trackEvent = (eventName: string, properties?: PostHogEventProperties): void => {
  const postHog = getPostHogClient();
  if (!postHog) return;
  try {
    postHog.capture(eventName, {timestamp: new Date().toISOString(), ...properties});
  } catch (error) {
    console.error('Failed to capture PostHog event:', error);
  }
};

export const trackUser = (userId: string, userProperties?: PostHogUserProperties): void => {
  const postHog = getPostHogClient();
  if (!postHog) return;
  try {
    postHog.identify(userId, userProperties);
  } catch (error) {
    console.error('Failed to identify user in PostHog:', error);
  }
};

export const resetUser = (): void => {
  const postHog = getPostHogClient();
  if (!postHog) return;
  try {
    postHog.reset();
  } catch (error) {
    console.error('Failed to reset PostHog:', error);
  }
};

export const trackPageView = (path?: string): void => {
  const postHog = getPostHogClient();
  if (!postHog) return;
  try {
    postHog.capture('$pageview', {
      $current_url: path || (typeof window !== 'undefined' ? window.location.href : ''),
    });
  } catch (error) {
    console.error('Failed to capture page view in PostHog:', error);
  }
};

export const trackUserLogin = (userId: string, properties?: PostHogEventProperties): void => {
  trackEvent('user_logged_in', {user_id: userId, ...properties});
};

export const trackPlanUpgrade = (
  fromPlan: string,
  toPlan: string,
  properties?: PostHogEventProperties
): void => {
  trackEvent('plan_upgraded', {from_plan: fromPlan, to_plan: toPlan, ...properties});
};

export const trackFeatureUsed = (
  featureName: string,
  properties?: PostHogEventProperties
): void => {
  trackEvent('feature_used', {feature_name: featureName, ...properties});
};

export const trackIntegrationConnected = (
  integration: string,
  properties?: PostHogEventProperties
): void => {
  trackEvent('integration_connected', {integration_name: integration, ...properties});
};

export const trackSegmentCreated = (
  segmentType: string,
  properties?: PostHogEventProperties
): void => {
  trackEvent('segment_created', {segment_type: segmentType, ...properties});
};

export const trackOnboardingStepCompleted = (
  stepKey: string,
  properties?: PostHogEventProperties
): void => {
  trackEvent('onboarding_step_completed_snapshot', {step_key: stepKey, ...properties});
};

export const trackOnboardingStepActionClicked = (
  stepKey: string,
  action: string,
  properties?: PostHogEventProperties
): void => {
  trackEvent('onboarding_step_action_clicked', {step_key: stepKey, action, ...properties});
};

export const setConnectionAttempt = (integrationSlug: string): void => {
  if (import.meta.client) {
    localStorage.setItem(`vector_recent_connection_${integrationSlug}`, 'true');
  }
};

export const checkAndTrackRecentConnection = (
  integrationSlug: string,
  integrationName: string,
  wasConnected: boolean,
  isConnected: boolean,
  additionalProperties: PostHogEventProperties = {}
): boolean => {
  if (!import.meta.client) return false;

  const recentConnection = localStorage.getItem(`vector_recent_connection_${integrationSlug}`);

  if (recentConnection && !wasConnected && isConnected) {
    trackIntegrationConnected(integrationName, {
      ...additionalProperties,
    });
    localStorage.removeItem(`vector_recent_connection_${integrationSlug}`);
    return true;
  }

  return false;
};

export const getUserProperty = (key: string): unknown => {
  const postHog = getPostHogClient();
  if (!postHog) return undefined;
  try {
    const userProperties = postHog.get_property('$stored_person_properties') as Record<
      string,
      string | number | boolean
    >;

    return userProperties[key];
  } catch {
    return undefined;
  }
};

export const handleIntegrationConnection = {
  setAttempt: setConnectionAttempt,
  checkAndTrack: checkAndTrackRecentConnection,
};
